import { defineStore } from 'pinia';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import type {
  HierarchyLevelDto,
  HierarchyNodeDto,
  SizeSystemDto,
  UnitDto,
  SizeSystemDtoVersion,
  HierarchyNodeProductPlaceholderImageDto,
} from '@/ApiClient.ts';
import { Gender, HierarchyLevelType } from '@/ApiClient.ts';
import { computed } from 'vue';
import { useHierarchyLevels } from '@/store/hierarchy-levels.ts';
import { useUnits } from '@/store/units.ts';
import { useSizeSystems } from '@/store/size-systems.ts';
import { useTags } from '@/store/tags.ts';
import { useProductCategories } from '@/store/product-categories.ts';
import { hierarchyIcon, hierarchyIcons, loadHierarchyIcons } from '@lib/icons-imports/hierarchy-icons.ts';
import { useBrand } from '@/store/brand.ts';

export interface HierarchyNode extends HierarchyNodeDto {
  level: HierarchyLevelDto;
  parent?: HierarchyNode;
  parents: HierarchyNode[];
  children: HierarchyNode[];

  inheritedProductCategoryDisplayName?: string;
  inheritedMChartShortName?: string;
  inheritedMChartLongName?: string;

  defaultUnit?: UnitDto;
  inheritedUnit?: UnitDto;
  inheritedUnitAuto?: boolean;

  inheritedGenders?: Gender[];
  inheritedGendersAuto?: boolean;

  defaultSizeSystem?: SizeSystemDto;
  inheritedSizeSystem?: SizeSystemDto;
  inheritedSizeSystemAuto?: boolean;

  inheritedIcon?: string;
  inheritedProductPlaceholderImage?: HierarchyNodeProductPlaceholderImageDto;
  inheritedDefaultBomTemplateName?: string;
  fullNameOrName?: string;
}

/**
 * What to call a node in a table that has one per row. The qualified name: a bare one
 * repeats across the tree ("Jackets" under both Men and Women), and a label that names
 * two rows refuses every paste that mentions it.
 */
export const hierarchyNodeLabel = (node: HierarchyNode) => node.fullNameOrName ?? node.name;

export const useHierarchy = defineStore('hierarchy', () => {
  loadHierarchyIcons();

  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.hierarchyNodes_GetHierarchyNodes,
    dto: 'HierarchyNodeDto',
    create: api.hierarchyNodes_AddHierarchyNode,
    update: api.hierarchyNodes_UpdateHierarchyNode,
    delete: api.hierarchyNodes_DeleteHierarchyNode,
    signalR: 'HierarchyUpdate',
  });

  const nodesByParentId = computed(() => {
    const acc = {} as Record<number, HierarchyNodeDto[]>;
    globalStoreBase.loaded.value.forEach((node) => {
      const parentId = node.parentId ?? 0;
      if (acc[parentId] === undefined) acc[parentId] = [];

      acc[parentId].push(node);
    });

    return acc;
  });

  function getFullNode(node: HierarchyNodeDto, parent?: HierarchyNode): HierarchyNode {
    const defaultUnit = node.defaultUnitId ? useUnits().byId[node.defaultUnitId] : undefined;
    const defaultSizeSystem = node.defaultSizeSystemId ? useSizeSystems().byId[node.defaultSizeSystemId] : undefined;

    const fullNode: HierarchyNode = {
      ...node,
      level: parent?.level ? useHierarchyLevels().nextLevel(parent.level) : useHierarchyLevels().topLevel,
      parent,
      parents: parent ? [parent, ...parent.parents] : [],
      children: [],

      inheritedProductCategoryDisplayName:
        node.productCategoryDisplayName ?? parent?.inheritedProductCategoryDisplayName,
      inheritedMChartShortName: node.mChartShortName ?? parent?.inheritedMChartShortName,
      inheritedMChartLongName: node.mChartLongName ?? parent?.inheritedMChartLongName,

      defaultUnit,
      inheritedUnit: defaultUnit ?? parent?.inheritedUnit,
      inheritedUnitAuto: node.setUnitAutomatically ?? parent?.inheritedUnitAuto,

      inheritedGenders: node.defaultGenders ?? parent?.inheritedGenders,
      inheritedGendersAuto: node.setGendersAutomatically ?? parent?.inheritedGendersAuto,

      defaultSizeSystem,
      inheritedSizeSystem: defaultSizeSystem ?? parent?.inheritedSizeSystem,
      inheritedSizeSystemAuto: node.setSizeSystemAutomatically ?? parent?.inheritedSizeSystemAuto,

      inheritedIcon: node.icon ?? parent?.inheritedIcon,
      inheritedProductPlaceholderImage: node.productPlaceholderImage ?? parent?.inheritedProductPlaceholderImage,
      inheritedDefaultBomTemplateName: node.defaultBomTemplateName ?? parent?.inheritedDefaultBomTemplateName,

      fullNameOrName: node.fullName ?? node.name,
    };
    fullNode.children = nodesByParentId.value[node.id]?.map((child) => getFullNode(child, fullNode)) ?? [];

    return fullNode;
  }

  const tree = computed(() => {
    const rootNodes: HierarchyNode[] = [];

    nodesByParentId.value[0]?.forEach((node) => {
      rootNodes.push(getFullNode(node));
    });

    return rootNodes;
  });

  function getNodeAndAncestors(nodeId: number): HierarchyNode[] {
    const fullNode = fullNodesById.value[nodeId];
    return [fullNode, ...fullNode.parents];
  }

  function isSizeSystemValid(node: HierarchyNode, sizeSystemId: number): boolean {
    return getAllValidSizeSystemsForNode(node)
      .map((ss) => ss.id)
      .includes(sizeSystemId);
  }

  function isTagCategoryValid(node: HierarchyNode, tagCategoryId: number): boolean {
    const noRestrictions = useTags()
      .categoryAvailableOnAllNodes.map((tc) => tc.id)
      .includes(tagCategoryId);

    if (noRestrictions) return true;

    return getNodeAndAncestors(node.id)
      .flatMap((n) => n.tagCategoryIds)
      .includes(tagCategoryId);
  }

  function isProductCategoryValid(node: HierarchyNode, productCategoryId: number): boolean {
    const noRestrictions = useProductCategories()
      .categoryAvailableOnAllNodes.map((pc) => pc.id)
      .includes(productCategoryId);

    if (noRestrictions) return true;

    return getNodeAndAncestors(node.id)
      .flatMap((n) => n.productCategoryIds)
      .includes(productCategoryId);
  }

  function allChildren(node: HierarchyNode): HierarchyNode[] {
    return [node, ...node.children.flatMap(allChildren)];
  }
  const fullNodes = computed<HierarchyNode[]>(() => tree.value.flatMap(allChildren));
  const fullNodesById = computed(() => {
    const acc = {} as Record<number, HierarchyNode>;

    fullNodes.value.forEach((node) => {
      acc[node.id] = node;
    });

    return acc;
  });

  const primaryNodes = computed(() => fullNodes.value.filter((n) => n.level.type === HierarchyLevelType.Primary));
  const secondaryNodes = computed(() => fullNodes.value.filter((n) => n.level.type === HierarchyLevelType.Secondary));

  const primary = (node: HierarchyNode) =>
    [node, ...node.parents].find((p) => p.level.type === HierarchyLevelType.Primary);
  const secondary = (node: HierarchyNode) =>
    [node, ...node.parents].find((p) => p.level.type === HierarchyLevelType.Secondary);

  function getIconWithFallback(hierarchyNodeId?: number) {
    const fallback = hierarchyIcons['other'];
    if (!hierarchyNodeId) return fallback;

    const node = useHierarchy().fullNodesById[hierarchyNodeId];
    if (!node) return fallback;

    return hierarchyIcon(node.inheritedIcon);
  }

  const getAllValidSizeSystemsForNode = (node?: HierarchyNode): SizeSystemDto[] => {
    const availableOnAllNodes = useSizeSystems().sizeSystemsAvailableOnAllNodes;
    if (!node) return availableOnAllNodes;

    const sizeSystemsWithinFamily = getNodeAndAncestors(node.id)
      .flatMap((n) => n.sizeSystemIds)
      .map((id) => useSizeSystems().byId[id])
      .filter((sizeSystem) => sizeSystem !== undefined);

    // A node may link a size system its ancestor already links
    const byId = new Map([...availableOnAllNodes, ...sizeSystemsWithinFamily].map((ss) => [ss.id, ss]));
    return [...byId.values()];
  };

  const getDefaultSizeSystemVersion = (product: { hierarchyNodeId: number }): SizeSystemDtoVersion => {
    const hierarchyNode = fullNodesById.value[product.hierarchyNodeId];

    const sizeSystem = hierarchyNode.inheritedSizeSystem
      ? hierarchyNode.inheritedSizeSystem
      : getAllValidSizeSystemsForNode(hierarchyNode)[0];

    return useSizeSystems().getDefaultVersionOfSizeSystem(sizeSystem);
  };

  function getDefaultGenders(hierarchyNodeId?: number) {
    if (!hierarchyNodeId) return [];

    return fullNodesById.value[hierarchyNodeId].inheritedGenders;
  }

  const getProductPlaceholderImagePath = (nodeId: number | undefined): string => {
    if (nodeId) {
      const hierarchyNode = fullNodesById.value[nodeId];
      const image = hierarchyNode.inheritedProductPlaceholderImage;
      if (image) return image.path;
    }

    return useBrand().brand?.logoUrl ?? '';
  };

  function getRootNode() {
    return fullNodes.value.find((n) => n.parent === undefined);
  }

  function mChartName(hierarchyNodeId: number) {
    const inheritedName = fullNodesById.value[hierarchyNodeId]?.inheritedMChartLongName;
    return inheritedName?.trim() ? inheritedName.trim() : 'Measurement list';
  }

  function mChartShortName(hierarchyNodeId: number) {
    const inheritedShortName = fullNodesById.value[hierarchyNodeId]?.inheritedMChartShortName;
    return inheritedShortName?.trim() ? inheritedShortName.trim() : 'MList';
  }

  return {
    ...globalStoreBase,

    tree,
    fullNodes,
    fullNodesById,

    primaryNodes,
    secondaryNodes,

    primary,
    secondary,
    getNodeAndAncestors,
    allChildren,
    getRootNode,

    isSizeSystemValid,
    isTagCategoryValid,
    isProductCategoryValid,

    getIconWithFallback,
    getDefaultSizeSystemVersion,
    getDefaultGenders,
    getAllValidSizeSystemsForNode,
    getProductPlaceholderImagePath,

    mChartName,
    mChartShortName,

    hierarchyIcon,
    hierarchyIcons,
  };
});
